import { Box, Button, Divider, IconButton, Stack } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMessage } from "../../../../base/MessageProvider";
import { useDispatch, useSelector } from "react-redux";
import { KeyboardBackspace } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import ReactTextInput from "../../../../components/ControllerComponent/ReactTextInput";
import ReactSelect from "../../../../components/ControllerComponent/ReactSelect";
import {
  genderFullOptions,
  languagesOptions,
  jobTypes,
  basicProfilecategory,
  artistCategoryOptions,
} from "../../../../constants";
import ReactSlider from "../../../../components/ControllerComponent/ReactSlider";
import validationSchema from "./validation";
import { addNewRollData, updateRollData } from "../../../actions/project";
import ReactCheckBox from "../../../../components/ControllerComponent/ReactCheckBox";
import ReactLocationInput from "../../../../components/ControllerComponent/ReactLocationInput";
import ReactRadio from "../../../../components/ControllerComponent/ReactRadio";
import ImageSection from "../ImageSection";
import VideoSection from "../VideoSection";
import VoiceNoteSection from "../VoiceNoteSection";
import PdfSection from "../PdfSection";
import ReactiveDatePickerField from "../../../../components/ControllerComponent/ReactDatePicker";
import ReactSelectAll from "../../../../components/ControllerComponent/ReactSelectAll";

const RoleForm = (props) => {
  const { open, navigation, route } = props;
  const [activeJobTypes, setActiveJobTypes] = useState("OPEN_AUDITION");

  const alert = useMessage();
  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    clearErrors,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      language: [],
      experienceCategory: [],
      negotiable: false,
      selfTestCheck: "no",
      isExpectedBudget: "no",
      isActor: false,
      isModel: false,
      isInfluencer: false,
    },
  });

  const selectedRoleList =
    useSelector((state) => state.projectReducer.selectedRoleList) ?? [];

  const profileCategoryList = useSelector(
    (state) => state.profileCategoryReducer?.profileCategoryList ?? []
  );

  const profileCategoryOptions = useMemo(
    () =>
      profileCategoryList.map((item) => ({ label: item.name, value: item.id })),
    [profileCategoryList]
  );

  const actorProfile = useMemo(
    () => profileCategoryList.find((item) => item.name === "Actor"),
    [profileCategoryList]
  );

  const roleTypeList = useSelector(
    (state) => state.roleTypeReducer?.roleTypeList ?? []
  );

  const roleTypeOptions = useMemo(
    () => roleTypeList.map((item) => ({ label: item.name, value: item.id })),
    [roleTypeList]
  );

  const locationList = useSelector(
    (state) => state.projectReducer?.locationList ?? []
  );

  const artistExperienceCategoryList =
    useSelector(
      (state) => state.generalReducer?.artistExperienceCategoryList
    ) ?? [];

  const artistExperienceCategoriesOption = useMemo(
    () =>
      artistExperienceCategoryList.map((item) => ({
        label: item,
        value: item,
      })),
    [artistExperienceCategoryList]
  );

  const metadata = route.params?.metadata;
  const selectedIndex = route.params?.index;

  const selectedArtistCategory = watch("profileCategoryId");
  const selfTestValue = watch("selfTestCheck");
  const isActor = selectedArtistCategory === basicProfilecategory.ACTOR;
  const isModel = selectedArtistCategory === basicProfilecategory.MODEL;
  const isInfluencer =
    selectedArtistCategory === basicProfilecategory.INFLUENCER;

  const [storedImages, setStoredImages] = useState([]);
  const [storedVideos, setStoredVideos] = useState([]);
  const [storedAudios, setStoredAudios] = useState([]);
  const [storedPdf, setStoredPdf] = useState([]);

  useEffect(() => {
    if (open && navigation.route === "RoleForm") {
      console.log("metadata------>>>>>", metadata);
      clearErrors();
      reset();
      setValue("roleTypeId", metadata?.roleType?.id ?? "");
      setValue(
        "profileCategoryId",
        metadata?.profileCategory?.id ?? actorProfile?.id ?? ""
      );
      setValue(
        "isActor",
        metadata?.profileCategories?.some(
          (a) => a.id === basicProfilecategory.ACTOR
        )
      );
      setValue(
        "isModel",
        metadata?.profileCategories?.some(
          (a) => a.id === basicProfilecategory.MODEL
        )
      );
      setValue(
        "isInfluencer",
        metadata?.profileCategories?.some(
          (a) => a.id === basicProfilecategory.INFLUENCER
        )
      );
      setValue("gender", metadata?.gender ?? "");
      setValue("ageRange", [+metadata?.minAge || 10, +metadata?.maxAge || 60]);
      setValue("characterDescription", metadata?.characterDescription ?? "");
      setValue("shootLocation", metadata?.shootLocation ?? "");
      const shootDate = metadata?.shootDate
        ? new Date(metadata?.shootDate)
        : null;

      if (shootDate && shootDate < new Date()) {
        setValue("shootDate", null); // Don't set past shoot dates
      } else {
        setValue("shootDate", shootDate);
      }
      setValue(
        "experienceCategory",
        metadata?.experienceCategory?.split(",") ?? []
      );
      setValue("language", metadata?.language?.split(", ") ?? []);
      setValue("auditionLocation", metadata?.auditionLocation ?? "");
      setValue("numberOfDays", metadata?.numberOfDays?.toString() ?? "");
      setValue("minBudget", metadata?.minBudget?.toString() ?? "");
      setValue("maxBudget", metadata?.maxBudget?.toString() ?? "");
      setValue("payBasis", metadata?.payBasis ?? "");
      setValue("script", metadata?.script ?? "");
      setValue("selfTestCheck", "no");
      setValue("isExpectedBudget", "no");
      setValue("selfTestCheck", metadata?.selfTestCheck ? "yes" : "no");
      setValue("isExpectedBudget", metadata?.isExpectedBudget ? "yes" : "no");
      setStoredImages(metadata?.storedImages ?? []);
      setStoredAudios(metadata?.storedAudios ?? []);
      setStoredVideos(metadata?.storedVideos ?? []);
      setStoredPdf(metadata?.storedPdf ?? []);
      setActiveJobTypes(metadata?.jobType ?? "OPEN_AUDITION");
    }
  }, [open, metadata, navigation.route]);

  const onSuccessCallBack = () => {
    navigation?.goBack();
  };

  const onsubmit = async (data) => {
    if (selfTestValue === "yes") {
      if (
        storedImages.length === 0 &&
        storedVideos.length === 0 &&
        storedAudios.length === 0 &&
        storedPdf.length === 0
      ) {
        alert.showError("Add atleast single media", { timeout: 10000 });
        return;
      }
    }

    try {
      const profileCategories = [];
      if (data.isActor && data.profileCategoryId !== basicProfilecategory.ACTOR)
        profileCategories.push({ id: basicProfilecategory.ACTOR });
      if (data.isModel && data.profileCategoryId !== basicProfilecategory.MODEL)
        profileCategories.push({ id: basicProfilecategory.MODEL });
      if (
        data.isInfluencer &&
        data.profileCategoryId !== basicProfilecategory.INFLUENCER
      )
        profileCategories.push({ id: basicProfilecategory.INFLUENCER });

      const roleName = roleTypeList.find(
        (item) => item.id === data.roleTypeId
      )?.name;
      const categoryName = profileCategoryList.find(
        (item) => item.id === data.profileCategoryId
      )?.name;
      const gender = data.gender;

      const title = `${gender} ${categoryName} for ${roleName} role`;

      if (metadata) {
        dispatch(
          updateRollData(
            {
              ...data,
              title: metadata?.title ?? title,
              profileCategories,
              jobType: activeJobTypes,
              storedAudios,
              storedImages,
              storedVideos,
              storedPdf,
              selfTestCheck: selfTestValue === "yes",
              isExpectedBudget: data.isExpectedBudget === "yes",
              index: selectedIndex,
            },
            selectedRoleList,
            onSuccessCallBack
          )
        );
      } else {
        dispatch(
          addNewRollData(
            {
              ...data,
              title,
              profileCategories,
              jobType: activeJobTypes,
              storedAudios,
              storedImages,
              storedVideos,
              storedPdf,
              selfTestCheck: selfTestValue === "yes",
              isExpectedBudget: data.isExpectedBudget === "yes",
            },
            selectedRoleList,
            onSuccessCallBack
          )
        );
      }
    } catch (error) {
      console.log(error);
      if (error?.message) alert.showError(error.message, { timeout: 10000 });
    }
  };

  const getJobTypesStyle = (id) => ({
    backgroundColor: "#2F80ED10",
    padding: "20px",
    borderRadius: "999px",
    display: "flex",
    alignItems: "center",
    border: id === activeJobTypes ? "1px solid #2F80ED" : "none",
  });

  const getLabelTextStyle = (id) => {
    return id === activeJobTypes
      ? "text-[10px] text-[#2F80ED] font-bold"
      : "text-[10px] text-[#2F80ED]";
  };

  const handleSaveImage = async (images) => {
    try {
      if (storedImages.length + images.length > 5) {
        alert.showError("You can only store up to 5 images", {
          timeout: 10000,
        });
        return;
      }

      setStoredImages((prevImages) => [...prevImages, ...images]);
      alert.showSuccess("Media added successfully");
    } catch (error) {
      alert.showError("Error adding images", { timeout: 10000 });
    }
  };

  const handleSaveVideo = async (video) => {
    try {
      if (storedVideos.length >= 5) {
        alert.showError("You can only store up to 5 videos", {
          timeout: 10000,
        });
        return;
      }
      const videoUrl = video;

      setStoredVideos((prevVideos) => [...prevVideos, videoUrl]);
      alert.showSuccess("Media added successfully");
    } catch (error) {
      alert.showError("Error adding video", { timeout: 10000 });
    }
  };

  const handleSaveAudio = async (audios) => {
    console.log("audios", audios);
    try {
      const MAX_AUDIO_SIZE_MB = 5;
      const MAX_AUDIO_COUNT = 5;

      // Check if any file exceeds 5MB
      const oversizedAudio = audios.find(
        (audio) => audio.size / (1024 * 1024) > MAX_AUDIO_SIZE_MB
      );
      if (oversizedAudio) {
        alert.showError(`The file "${oversizedAudio.name}" exceeds 5MB limit`, {
          timeout: 10000,
        });
        return;
      }

      // Check if the total stored audios exceed 5
      if (storedAudios.length + audios.length > MAX_AUDIO_COUNT) {
        alert.showError("You can only store up to 5 audios", {
          timeout: 10000,
        });
        return;
      }

      setStoredAudios((prevAudios) => [...prevAudios, ...audios]);
      alert.showSuccess("Media added successfully");
    } catch (error) {
      console.error("Error adding audios:", error);
      alert.showError("Error adding audios", { timeout: 10000 });
    }
  };

  const handleSavePdf = async (pdf) => {
    console.log("pdf", pdf);
    try {
      if (storedPdf.length >= 5) {
        alert.showError("You can only store up to 5 PDFs", { timeout: 10000 });
        return;
      }

      setStoredPdf((prevPdf) => [...prevPdf, pdf]);
      alert.showSuccess("Media added successfully");
    } catch (error) {
      alert.showError("Error adding pdf", { timeout: 10000 });
    }
  };

  const removeImageByName = (indexToRemove) => {
    const updatedData = storedImages;
    updatedData.splice(indexToRemove, 1);

    setStoredImages([...updatedData]);
  };

  const removeVideoByIndex = (indexToRemove) => {
    const updatedData = storedVideos;
    updatedData.splice(indexToRemove, 1);

    setStoredVideos([...updatedData]);
  };

  const removeAudioByIndex = (indexToRemove) => {
    const updatedData = storedAudios;
    updatedData.splice(indexToRemove, 1);

    setStoredAudios([...updatedData]);
  };

  const removPdfByIndex = (indexToRemove) => {
    const updatedData = storedPdf;
    updatedData.splice(indexToRemove, 1);

    setStoredPdf([...updatedData]);
  };

  const onError = (errors) => {
    const missingFields = Object.keys(errors);

    if (missingFields.length > 1) {
      alert.showError("One or more details are missing", { timeout: 10000 });
    } else if (missingFields.length === 1) {
      const fieldName = missingFields[0];
      let missingFieldName = "";

      if (fieldName === "auditionLocation")
        missingFieldName = "Audition Location";
      else if (fieldName === "experienceCategory")
        missingFieldName = "Experience Category";
      else if (fieldName === "characterDescription")
        missingFieldName = "Character Description";
      else if (fieldName === "roleTypeId") missingFieldName = "Role Type";
      else if (fieldName === "gender") missingFieldName = "Gender";
      else if (fieldName === "script") missingFieldName = "Script";
      else if (fieldName === "numberOfDays") {
        missingFieldName = "Number of days";
        alert.showError(
          `${missingFieldName} ${errors?.numberOfDays?.message}`,
          { timeout: 10000 }
        );
        return;
      }

      alert.showError(`${missingFieldName} is required`, { timeout: 10000 });
    }
  };

  return (
    <Box className="h-full w-full flex flex-col justify-between pb-7 overflow-y-auto">
      <div>
        <div className="flex items-center justify-between py-2 px-4">
          <IconButton onClick={navigation?.goBack}>
            <KeyboardBackspace />
          </IconButton>
          <h2 className="w-full text-center font-['Poppins-Semibold'] leading-[25px] text-[14px] ">
            {metadata ? "EDIT" : "ADD"} ROLE
          </h2>
        </div>
        <Divider />
        <Stack px={2} py={4} spacing={2}>
          <ReactSelect
            hookFormControl={control}
            label="Artist Category*"
            varName="profileCategoryId"
            key={"profileCategoryId"}
            withSelect={false}
            options={profileCategoryOptions}
            onChange={() => {
              setValue("isActor", false);
              setValue("isModel", false);
              setValue("isInfluencer", false);
            }}
            errorMessage={errors.profileCategoryId?.message}
          />

          {!isInfluencer && selectedArtistCategory && (
            <Stack space={1} alignItems={"center"}>
              <Stack
                direction={"row"}
                display={selectedArtistCategory ? "flex" : "none"}
                width={"100%"}
                alignItems={"center"}
                justifyContent={"flex-start"}
                space={4}
              >
                {isModel && (
                  <ReactCheckBox
                    hookFormControl={control}
                    label="Actor"
                    varName="isActor"
                    key={"isActor"}
                  />
                )}
                {isActor && (
                  <ReactCheckBox
                    hookFormControl={control}
                    label="Model"
                    varName="isModel"
                    key={"isModel"}
                  />
                )}
                {!isInfluencer && (
                  <ReactCheckBox
                    hookFormControl={control}
                    label="Influencer"
                    varName="isInfluencer"
                    key={"isInfluencer"}
                  />
                )}
              </Stack>
            </Stack>
          )}

          <ReactSelectAll
            name="experienceCategory"
            control={control}
            label="Select Experience Category"
            data={artistExperienceCategoriesOption}
            multiSelect
            showSelectAll
            error={errors.experienceCategory?.message}
          />
          <ReactSelect
            hookFormControl={control}
            label="Role Type*"
            varName="roleTypeId"
            key={"roleTypeId"}
            withSelect={false}
            options={roleTypeOptions}
            errorMessage={errors?.roleTypeId?.message}
          />
          <ReactSelect
            hookFormControl={control}
            label="Gender*"
            varName="gender"
            key={"gender"}
            options={genderFullOptions}
            errorMessage={errors?.gender?.message}
          />
          <ReactSlider
            hookFormControl={control}
            label="Age Range"
            varName="ageRange"
            key={"ageRange"}
            minValue={0}
            maxValue={75}
            sx={{ color: "#000" }}
            errorMessage={errors?.ageRange?.message}
          />
          <ReactSelect
            hookFormControl={control}
            label="Language"
            varName="language"
            key={"language"}
            isMultiSelect
            options={languagesOptions}
            errorMessage={errors?.language?.message}
          />
          <ReactTextInput
            hookFormControl={control}
            label={"Character Description*"}
            varName="characterDescription"
            key={"characterDescription"}
            rows={3}
            maxLength={600}
            isMultiLine
            errorMessage={errors?.characterDescription?.message}
          />
          <ReactLocationInput
            hookFormControl={control}
            placeholder="Eg: Bhopal, Madhya Pradesh"
            label="Audition Location"
            varName="auditionLocation"
            key={"auditionLocation"}
            options={locationList}
            errorMessage={errors?.auditionLocation?.message}
          />
          <ReactLocationInput
            placeholder="Eg: Pune, Maharashtra"
            hookFormControl={control}
            label="Shoot Location"
            varName="shootLocation"
            errorMessage={errors?.shootLocation?.message}
            options={locationList}
          />
          <ReactiveDatePickerField
            hookFormControl={control}
            label={"Shoot Date"}
            varName={"shootDate"}
            errorMessage={errors?.shootDate?.message}
            minDate={new Date()}
          />
          <ReactTextInput
            hookFormControl={control}
            placeholder="Eg: 25"
            label="Number Of Days"
            varName="numberOfDays"
            key={"numberOfDays"}
            dataType="number"
            errorMessage={errors?.numberOfDays?.message}
          />
          <Stack direction={"row"} className="gap-1">
            <ReactTextInput
              hookFormControl={control}
              label="Min Budget"
              varName="minBudget"
              key={"minBudget"}
              dataType="number"
              disabled={watch("negotiable")}
              errorMessage={
                errors?.minBudget?.message || errors?.maxBudget?.message
              }
            />
            <ReactTextInput
              hookFormControl={control}
              label="Max Budget"
              varName="maxBudget"
              key={"maxBudget"}
              dataType="number"
              disabled={watch("negotiable")}
            />
          </Stack>
          <ReactCheckBox
            hookFormControl={control}
            label="Depends on Artist?"
            varName="negotiable"
            key={"negotiable"}
          />
          <ReactTextInput
            hookFormControl={control}
            placeholder="Eg. Per Month, Per Day"
            label="Pay Basis"
            varName="payBasis"
            key={"payBasis"}
            errorMessage={errors?.payBasis?.message}
          />
        </Stack>

        <Stack px={2} spacing={2}>
          <div className="border-[2px] border-[#E8E8E9] px-3 rounded-[8px] space-y-4">
            <Box className="w-full flex flex-wrap bg-white rounded-[12px]">
              <h2 className="w-full font-['Poppins-Semibold'] leading-[30px] text-[12px]">
                Self Test Required
              </h2>
              <ReactRadio
                hookFormControl={control}
                varName="selfTestCheck"
                options={[
                  { label: "Yes", value: "yes" },
                  { label: "No", value: "no" },
                ]}
                orientation="horizontal"
              />
            </Box>

            {selfTestValue === "yes" && (
              <>
                <Stack
                  direction={"row"}
                  spacing={2}
                  flex={1}
                  justifyContent="space-between"
                >
                  {jobTypes.map((cylinder) => (
                    <Button
                      key={cylinder.id}
                      disableRipple
                      sx={{
                        ...getJobTypesStyle(cylinder.id),
                        flex: 1,
                      }}
                      onClick={() => setActiveJobTypes(cylinder.id)}
                    >
                      <p className={getLabelTextStyle(cylinder.id)}>
                        {cylinder.label}
                      </p>
                    </Button>
                  ))}
                </Stack>

                <ReactTextInput
                  hookFormControl={control}
                  label={"Script*"}
                  varName="script"
                  key={"script"}
                  rows={3}
                  maxLength={1500}
                  isMultiLine
                  errorMessage={errors?.script?.message}
                />

                <PdfSection
                  pdfs={storedPdf}
                  onUpload={handleSavePdf}
                  onRemove={removPdfByIndex}
                />

                <VoiceNoteSection
                  audios={storedAudios}
                  onUpload={handleSaveAudio}
                  onRemove={removeAudioByIndex}
                />

                <ImageSection
                  images={storedImages}
                  onUpload={handleSaveImage}
                  onRemove={removeImageByName}
                />

                <VideoSection
                  videos={storedVideos}
                  onRemove={removeVideoByIndex}
                  onUpload={handleSaveVideo}
                />

                <div>
                  <h2 className="w-full font-['Poppins-Semibold'] leading-[20px] text-[14px]">
                    Do you want to ask artist about expected budget?
                  </h2>
                  <ReactRadio
                    hookFormControl={control}
                    varName="isExpectedBudget"
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" },
                    ]}
                    orientation="horizontal"
                  />
                </div>
              </>
            )}
          </div>
        </Stack>
        <div className="flex flex-col justify-between items-center gap-2 py-5 px-5">
          <Button
            onClick={handleSubmit(onsubmit, onError)}
            variant="contained"
            className="font-['Poppins-Medium'] normal-case text-[14px] text-white"
          >
            {metadata ? "UPDATE" : "Done"}
          </Button>
        </div>
      </div>
    </Box>
  );
};

export default RoleForm;
